/**
 * The company's mark in the app chrome.
 *
 * ⚠ THE SAME ASSET THE LANDING PAGES USE, NOT A SECOND COPY. It is the SVG
 * stored on agileandco.com's brand record: supplied by Grace on 2026-08-17 as
 * "White on Transparent.svg", and repaired by the landing work on 2026-08-18
 * because the supplied export painted an opaque white rectangle over the full
 * artboard, hiding the white mark. Core drawing its own version of that would
 * be the same defect as three copies of one matcher: two brands that agree
 * today and drift the first time somebody re-exports.
 *
 * ⚠ AN EARLIER PASS USED THE WRONG MARK ENTIRELY — the old script "Agile&Co"
 * wordmark, which was simply the artwork sitting in the repo. That file was
 * also an AI-generated placeholder called
 * `Gemini_Generated_Image_g8aq6yg8aq6yg8aq.png`, 1,029KB, rendered at 32px and
 * cropped square so it read as an illegible smudge. Neither the file nor the
 * design was the current brand. The revamp lockup is AGILE & CO over POWERED
 * BY CORE — and that tagline is worth noticing, because the brand itself
 * states this product's relationship to the company.
 *
 * ⚠ ONE POLARITY IS PUBLISHED, THE OTHER IS DERIVED AT THE SOURCE. The mark is
 * forced white by a colour matrix rather than by fills, so the dark variant
 * flips those three constants to 0 and leaves the luminance-to-alpha matrix
 * alone — the anti-aliased edges keep their coverage exactly. Recolouring the
 * source beats a CSS invert, which would have to be undone anywhere the mark
 * sits on a coloured ground.
 */
import glyphDark from "@/assets/agileco-glyph.png";
import glyphLight from "@/assets/agileco-glyph-light.png";
import darkArt from "@/assets/agileco-mark.svg";
import lightArt from "@/assets/agileco-mark-light.svg";
import { cn } from "@/lib/utils";

interface BrandMarkProps {
	/** Rendered height in px. The width follows the mark's real aspect. */
	height?: number;
	className?: string;
	/** Hidden from assistive tech when a visible text label sits beside it. */
	labelled?: boolean;
	/**
	 * Square crop for the collapsed icon rail.
	 *
	 * ⚠ IT IS THE LEADING PAIR, NOT AN INITIAL, AND THAT IS A CONSTRAINT OF THE
	 * ARTWORK. The mark is a connected script: scanning it for a column of
	 * empty pixels between letters finds none, because the letterforms join.
	 * There is no single "A" to cut out, so the rail carries the opening
	 * strokes of the real face rather than a monogram invented in a text
	 * editor to look like one.
	 */
	glyph?: boolean;
}

export function BrandMark({
	height = 28,
	className,
	labelled = true,
	glyph = false,
}: BrandMarkProps) {
	const [dark, light] = glyph ? [glyphDark, glyphLight] : [darkArt, lightArt];
	return (
		<span
			className={cn("inline-flex shrink-0 items-center", className)}
			style={{ height }}
		>
			{/* Two files rather than a CSS filter: `invert()` also inverts the
			    anti-aliased edge pixels, which fringes a script face at small
			    sizes. The knockout is generated from the source art instead. */}
			<img
				src={dark}
				alt={labelled ? "Agile & Co." : ""}
				aria-hidden={labelled ? undefined : true}
				style={{ height }}
				className="w-auto dark:hidden"
			/>
			<img
				src={light}
				alt={labelled ? "Agile & Co." : ""}
				aria-hidden={labelled ? undefined : true}
				style={{ height }}
				className="hidden w-auto dark:block"
			/>
		</span>
	);
}
